import React from "react";
import PropTypes from "prop-types";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Collapse from "@mui/material/Collapse";
import IconButton from "@mui/material/IconButton";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import CampaignOutlinedIcon from "@mui/icons-material/CampaignOutlined";
import CloseIcon from "@mui/icons-material/Close";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";

const STORAGE_KEY = "ouro.refragPromoDismissed.v1";
const REFRAG_REDEEM_URL = "https://play.refrag.gg/redeem";
const REFRAG_PROMO_CODE = "ouro";
const REFRAG_EXPLAINER_TEXT =
  "Using Refrag to practice for CS2 competitive matches has a lot of advantages. It lets you train in realistic in-game situations, so your aim, movement, crosshair placement, utility usage, and prefire routes all improve in a way that directly transfers to real matches. Refrag is especially useful because it helps you practice specific weaknesses instead of just playing random deathmatch or matchmaking. With tools for warmups, utility lineups, positioning, demo review, and structured training modes, players can build better habits and feel more prepared before going into comp. Overall, Refrag makes practice more focused, efficient, and match-relevant.";

function readDismissed() {
  if (typeof window === "undefined" || !window.localStorage) {
    return false;
  }
  try {
    return window.localStorage.getItem(STORAGE_KEY) === "1";
  } catch (_error) {
    return false;
  }
}

function persistDismissal() {
  if (typeof window === "undefined" || !window.localStorage) {
    return;
  }
  try {
    window.localStorage.setItem(STORAGE_KEY, "1");
  } catch (_error) {
    // localStorage may be unavailable in privacy-restricted contexts.
  }
}

function PromoContent({ compact = false }) {
  return (
    <React.Fragment>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: compact ? 1 : 0.75 }}>
        <CampaignOutlinedIcon sx={{ color: "#A78BFA", fontSize: compact ? 18 : 20 }} />
        <Typography
          component="h2"
          sx={{
            color: "#ffffff",
            fontSize: compact ? "0.98rem" : "0.95rem",
            fontWeight: 700,
            lineHeight: 1.2,
          }}
        >
          Refrag partner offer
        </Typography>
      </Box>
      <Typography
        variant="body2"
        sx={{
          color: compact ? "rgba(255,255,255,0.78)" : "rgba(255,255,255,0.74)",
          lineHeight: 1.55,
        }}
      >
        Get a 3-day trial of Refrag Competitor plus 15% off a Refrag subscription. Redeem it at{" "}
        <Box
          component="a"
          href={REFRAG_REDEEM_URL}
          target="_blank"
          rel="noreferrer noopener"
          sx={{ color: "#E8A33D", textDecoration: "underline" }}
        >
          play.refrag.gg/redeem
        </Box>{" "}
        with code <strong style={{ color: "#ffffff" }}>{REFRAG_PROMO_CODE}</strong>.
      </Typography>
    </React.Fragment>
  );
}

PromoContent.propTypes = {
  compact: PropTypes.bool,
};

export default function RefragPromoNotice({
  variant = "banner",
  dismissible = false,
}) {
  const [dismissed, setDismissed] = React.useState(
    () => dismissible && readDismissed(),
  );
  const [showExplainer, setShowExplainer] = React.useState(false);
  const [codeCopied, setCodeCopied] = React.useState(false);

  const dismissPromo = React.useCallback(() => {
    persistDismissal();
    setDismissed(true);
  }, []);

  const copyPromoCode = React.useCallback(async () => {
    try {
      if (typeof navigator?.clipboard?.writeText !== "function") {
        return;
      }
      await navigator.clipboard.writeText(REFRAG_PROMO_CODE);
      setCodeCopied(true);
      window.setTimeout(() => setCodeCopied(false), 1600);
    } catch (_error) {
      setCodeCopied(false);
    }
  }, []);

  if (dismissible && dismissed) {
    return null;
  }

  if (variant === "card") {
    return (
      <Paper
        variant="outlined"
        sx={{
          p: 2,
          borderRadius: 2,
          borderColor: "rgba(232,163,61,0.24)",
          backgroundColor: "rgba(232,163,61,0.08)",
        }}
      >
        <PromoContent compact />
        <Box sx={{ mt: 1.5, display: "flex", flexWrap: "wrap", gap: 1 }}>
          <Chip
            size="small"
            label={`Code: ${REFRAG_PROMO_CODE}`}
            sx={{
              backgroundColor: "rgba(255,255,255,0.08)",
              color: "#ffffff",
              fontWeight: 700,
            }}
          />
          <Button
            size="small"
            variant="outlined"
            startIcon={<ContentCopyIcon fontSize="small" />}
            onClick={copyPromoCode}
            sx={{
              textTransform: "none",
              borderColor: codeCopied ? "rgba(76,175,80,0.5)" : "rgba(255,255,255,0.16)",
              color: codeCopied ? "#4CAF50" : "rgba(255,255,255,0.82)",
              "&:hover": {
                borderColor: codeCopied ? "#4CAF50" : "rgba(255,255,255,0.28)",
                backgroundColor: codeCopied ? "rgba(76,175,80,0.08)" : "rgba(255,255,255,0.04)",
              },
            }}
          >
            {codeCopied ? "Copied" : "Copy code"}
          </Button>
          <Button
            href={REFRAG_REDEEM_URL}
            target="_blank"
            rel="noreferrer noopener"
            size="small"
            variant="outlined"
            endIcon={<OpenInNewIcon fontSize="small" />}
            sx={{
              textTransform: "none",
              borderColor: "rgba(232,163,61,0.32)",
              color: "#E8A33D",
              "&:hover": {
                borderColor: "#F4B752",
                backgroundColor: "rgba(232,163,61,0.08)",
              },
            }}
          >
            Redeem on Refrag
          </Button>
        </Box>
        <Box
          sx={{
            mt: 1.25,
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            gap: 1.5,
          }}
        >
          <Button
            size="small"
            onClick={() => setShowExplainer((prev) => !prev)}
            endIcon={showExplainer ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            sx={{
              px: 0,
              textTransform: "none",
              justifyContent: "flex-start",
              color: "#E8A33D",
              "&:hover": {
                backgroundColor: "transparent",
                color: "#F4B752",
              },
            }}
          >
            What is refrag
          </Button>
          {dismissible ? (
            <Button
              size="small"
              onClick={dismissPromo}
              startIcon={<CloseIcon fontSize="small" />}
              sx={{
                px: 0,
                textTransform: "none",
                justifyContent: "flex-start",
                color: "rgba(255,255,255,0.62)",
                "&:hover": {
                  backgroundColor: "transparent",
                  color: "#ffffff",
                },
              }}
            >
              Dismiss
            </Button>
          ) : null}
          <Collapse in={showExplainer} sx={{ flexBasis: "100%" }}>
            <Typography
              variant="body2"
              sx={{
                mt: 1,
                color: "rgba(255,255,255,0.72)",
                lineHeight: 1.65,
              }}
            >
              {REFRAG_EXPLAINER_TEXT}
            </Typography>
          </Collapse>
        </Box>
      </Paper>
    );
  }

  return (
    <Alert
      icon={false}
      sx={{
        width: "100%",
        alignItems: "flex-start",
        borderRadius: 0,
        borderTop: "1px solid rgba(232,163,61,0.18)",
        borderBottom: "1px solid rgba(232,163,61,0.18)",
        backgroundColor: "rgba(232,163,61,0.08)",
        color: "#ffffff",
        "& .MuiAlert-message": {
          width: "100%",
          py: 0,
        },
      }}
      action={
        dismissible ? (
          <IconButton
            aria-label="Dismiss Refrag promo"
            size="small"
            onClick={dismissPromo}
            sx={{
              color: "rgba(255,255,255,0.56)",
              mt: 0.25,
              "&:hover": { color: "#ffffff" },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        ) : undefined
      }
    >
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: { xs: "flex-start", md: "center" },
          gap: 1.25,
          width: "100%",
        }}
      >
        <Box sx={{ flex: "1 1 360px", minWidth: 0 }}>
          <PromoContent />
        </Box>
        <Chip
          size="small"
          label={`Code: ${REFRAG_PROMO_CODE}`}
          sx={{
            backgroundColor: "rgba(255,255,255,0.08)",
            color: "#ffffff",
            fontWeight: 700,
            mt: { xs: 0.5, md: 0 },
          }}
        />
        <Button
          size="small"
          variant="outlined"
          startIcon={<ContentCopyIcon fontSize="small" />}
          onClick={copyPromoCode}
          sx={{
            textTransform: "none",
            borderColor: codeCopied ? "rgba(76,175,80,0.5)" : "rgba(255,255,255,0.16)",
            color: codeCopied ? "#4CAF50" : "rgba(255,255,255,0.82)",
            "&:hover": {
              borderColor: codeCopied ? "#4CAF50" : "rgba(255,255,255,0.28)",
              backgroundColor: codeCopied ? "rgba(76,175,80,0.08)" : "rgba(255,255,255,0.04)",
            },
          }}
        >
          {codeCopied ? "Copied" : "Copy code"}
        </Button>
        <Button
          href={REFRAG_REDEEM_URL}
          target="_blank"
          rel="noreferrer noopener"
          size="small"
          variant="contained"
          endIcon={<OpenInNewIcon fontSize="small" />}
          sx={{
            textTransform: "none",
            fontWeight: 700,
            backgroundColor: "#E8A33D",
            "&:hover": {
              backgroundColor: "#B97A1F",
            },
          }}
        >
          Redeem on Refrag
        </Button>
        <Box
          sx={{
            flexBasis: "100%",
            mt: 0.25,
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            gap: 1.5,
          }}
        >
          <Button
            size="small"
            onClick={() => setShowExplainer((prev) => !prev)}
            endIcon={showExplainer ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            sx={{
              px: 0,
              textTransform: "none",
              justifyContent: "flex-start",
              color: "#E8A33D",
              "&:hover": {
                backgroundColor: "transparent",
                color: "#F4B752",
              },
            }}
          >
            What is refrag
          </Button>
          {dismissible ? (
            <Button
              size="small"
              onClick={dismissPromo}
              startIcon={<CloseIcon fontSize="small" />}
              sx={{
                px: 0,
                textTransform: "none",
                justifyContent: "flex-start",
                color: "rgba(255,255,255,0.62)",
                "&:hover": {
                  backgroundColor: "transparent",
                  color: "#ffffff",
                },
              }}
            >
              Dismiss
            </Button>
          ) : null}
          <Collapse in={showExplainer} sx={{ flexBasis: "100%" }}>
            <Typography
              variant="body2"
              sx={{
                mt: 1,
                color: "rgba(255,255,255,0.72)",
                lineHeight: 1.65,
                maxWidth: 980,
              }}
            >
              {REFRAG_EXPLAINER_TEXT}
            </Typography>
          </Collapse>
        </Box>
      </Box>
    </Alert>
  );
}

RefragPromoNotice.propTypes = {
  dismissible: PropTypes.bool,
  variant: PropTypes.oneOf(["banner", "card"]),
};
